import React from "react";

/** Travel before a change of direction counts, so trackpad noise can't strobe the bar */
const TOLERANCE = 8;
/** Within this distance of the top the bar is always shown, and has no ground */
const KEEP = 120;

export interface TopbarScrollState {
    /** Hidden after scrolling down */
    away: boolean;
    /** Shown again after being away: returns on the quicker curve */
    back: boolean;
    /** Shown and past KEEP: the bar stands on a ground */
    solid: boolean;
    /** Its vertical middle is over the footer's dark band */
    onDark: boolean;
}

const INITIAL: TopbarScrollState = {
    away: false,
    back: false,
    solid: false,
    onDark: false,
};

/**
 * The 2026 topbar's scroll rules, after the prototype's `app.js` (hide and
 * ground) and `footer.js` (the inversion over the footer).
 */
export const useTopbarScroll = (
    barRef: React.RefObject<HTMLElement | null>,
): TopbarScrollState => {
    const [state, setState] = React.useState(INITIAL);

    React.useEffect(() => {
        const update = (patch: Partial<TopbarScrollState>) =>
            setState((prev) => {
                const next = { ...prev, ...patch };
                return next.away === prev.away &&
                    next.back === prev.back &&
                    next.solid === prev.solid &&
                    next.onDark === prev.onDark
                    ? prev
                    : next;
            });

        let lastY = Math.max(0, window.scrollY);
        let away = false;
        const onScroll = () => {
            const y = Math.max(0, window.scrollY);
            const dy = y - lastY;
            if (Math.abs(dy) < TOLERANCE) return;
            lastY = y;
            // Never while it holds focus
            const hide =
                dy > 0 && y > KEEP && !barRef.current?.matches(":focus-within");
            const patch: Partial<TopbarScrollState> = {
                // Asked on every accepted scroll, not only when the direction
                // changes, or the ground would stay on arriving back at the top
                solid: !hide && y > KEEP,
            };
            if (hide !== away) {
                away = hide;
                patch.away = hide;
                patch.back = !hide;
            }
            update(patch);
        };

        // The footer is found afresh each time, as it can mount after the bar
        const checkDark = () => {
            const bar = barRef.current;
            const footer = document.querySelector("footer");
            if (!bar || !footer) {
                update({ onDark: false });
                return;
            }
            // The middle of the bar where it rests (it is fixed at the top). Its
            // transformed box would still be on its way back mid-slide, and a
            // single scroll that returns it over the footer would miss the dark
            update({
                onDark:
                    footer.getBoundingClientRect().top <= bar.offsetHeight / 2,
            });
        };
        let queued = 0;
        const queueDark = () => {
            if (queued) return;
            queued = requestAnimationFrame(() => {
                queued = 0;
                checkDark();
            });
        };

        const onScrollEvent = () => {
            onScroll();
            queueDark();
        };

        // Keyboard focus moving into a hidden bar brings it back
        const onFocusIn = () => {
            if (!away) return;
            away = false;
            lastY = Math.max(0, window.scrollY);
            update({ away: false, back: true, solid: lastY > KEEP });
        };
        const bar = barRef.current;
        bar?.addEventListener("focusin", onFocusIn);

        window.addEventListener("scroll", onScrollEvent, { passive: true });
        window.addEventListener("resize", queueDark);
        // A page loaded already scrolled stands the bar on its ground
        update({ solid: lastY > KEEP });
        checkDark();
        return () => {
            bar?.removeEventListener("focusin", onFocusIn);
            window.removeEventListener("scroll", onScrollEvent);
            window.removeEventListener("resize", queueDark);
            cancelAnimationFrame(queued);
        };
    }, [barRef]);

    return state;
};
